import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" fill="none">
      <path d="M44.5 20H24v8.5h11.8C34.7 33.9 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-22 0-1.3-.2-2.7-.5-4z" fill="#FFC107"/>
      <path d="M6.3 14.7l7 5.1C15.2 16.1 19.3 13 24 13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 16.3 2 9.7 7.4 6.3 14.7z" fill="#FF3D00"/>
      <path d="M24 46c5.5 0 10.5-1.9 14.4-5.1l-6.7-5.5C29.7 36.7 27 37.5 24 37.5c-6 0-10.6-3.1-11.8-8.5l-7 5.4C8.6 41.3 15.8 46 24 46z" fill="#4CAF50"/>
      <path d="M44.5 20H24v8.5h11.8c-.6 2.7-2.2 4.9-4.5 6.4l6.7 5.5C42.4 36.8 45 31.2 45 24c0-1.3-.2-2.7-.5-4z" fill="#1976D2"/>
    </svg>
  );
}

/* ─── Icon components ───────────────────────────────────────────────────── */
function IconCheck({ className = 'w-3.5 h-3.5', style }) {
  return (
    <svg className={className} style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
function IconSpinner({ className = 'w-4 h-4' }) {
  return (
    <svg className={`${className} animate-spin`} viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}
function IconEye({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
function IconEyeOff({ className = 'w-4 h-4' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

/* ─── Password requirement row ──────────────────────────────────────────── */
function PwdReq({ met, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {met ? (
        <div style={{
          width: 16, height: 16, borderRadius: '50%',
          background: '#D1FAE5',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <IconCheck className="w-2.5 h-2.5" style={{ color: '#059669' }} />
        </div>
      ) : (
        <div style={{
          width: 16, height: 16, borderRadius: '50%',
          background: '#F1F5F9',
          border: '1.5px solid #CBD5E1',
          flexShrink: 0,
        }} />
      )}
      <span style={{
        fontSize: 12, fontWeight: 500,
        color: met ? '#059669' : '#64748B',
        transition: 'color 150ms',
      }}>
        {label}
      </span>
    </div>
  );
}

// ── Country dial codes ────────────────────────────────────────────────────────
const COUNTRIES = [
  // Top picks (platform-relevant markets)
  { code: '+971', flag: '🇦🇪', name: 'UAE' },
  { code: '+1',   flag: '🇺🇸', name: 'United States' },
  { code: '+44',  flag: '🇬🇧', name: 'United Kingdom' },
  { code: '+91',  flag: '🇮🇳', name: 'India' },
  { code: '+966', flag: '🇸🇦', name: 'Saudi Arabia' },
  { code: '+65',  flag: '🇸🇬', name: 'Singapore' },
  { code: '+61',  flag: '🇦🇺', name: 'Australia' },
  { code: '+92',  flag: '🇵🇰', name: 'Pakistan' },
  // ─ divider ─
  { code: '',     flag: '',    name: '──────────────' },
  // Alphabetical
  { code: '+213', flag: '🇩🇿', name: 'Algeria' },
  { code: '+43',  flag: '🇦🇹', name: 'Austria' },
  { code: '+973', flag: '🇧🇭', name: 'Bahrain' },
  { code: '+880', flag: '🇧🇩', name: 'Bangladesh' },
  { code: '+32',  flag: '🇧🇪', name: 'Belgium' },
  { code: '+55',  flag: '🇧🇷', name: 'Brazil' },
  { code: '+1',   flag: '🇨🇦', name: 'Canada' },
  { code: '+56',  flag: '🇨🇱', name: 'Chile' },
  { code: '+86',  flag: '🇨🇳', name: 'China' },
  { code: '+57',  flag: '🇨🇴', name: 'Colombia' },
  { code: '+45',  flag: '🇩🇰', name: 'Denmark' },
  { code: '+20',  flag: '🇪🇬', name: 'Egypt' },
  { code: '+251', flag: '🇪🇹', name: 'Ethiopia' },
  { code: '+358', flag: '🇫🇮', name: 'Finland' },
  { code: '+33',  flag: '🇫🇷', name: 'France' },
  { code: '+49',  flag: '🇩🇪', name: 'Germany' },
  { code: '+233', flag: '🇬🇭', name: 'Ghana' },
  { code: '+30',  flag: '🇬🇷', name: 'Greece' },
  { code: '+852', flag: '🇭🇰', name: 'Hong Kong' },
  { code: '+36',  flag: '🇭🇺', name: 'Hungary' },
  { code: '+62',  flag: '🇮🇩', name: 'Indonesia' },
  { code: '+98',  flag: '🇮🇷', name: 'Iran' },
  { code: '+964', flag: '🇮🇶', name: 'Iraq' },
  { code: '+353', flag: '🇮🇪', name: 'Ireland' },
  { code: '+972', flag: '🇮🇱', name: 'Israel' },
  { code: '+39',  flag: '🇮🇹', name: 'Italy' },
  { code: '+81',  flag: '🇯🇵', name: 'Japan' },
  { code: '+962', flag: '🇯🇴', name: 'Jordan' },
  { code: '+254', flag: '🇰🇪', name: 'Kenya' },
  { code: '+82',  flag: '🇰🇷', name: 'South Korea' },
  { code: '+965', flag: '🇰🇼', name: 'Kuwait' },
  { code: '+961', flag: '🇱🇧', name: 'Lebanon' },
  { code: '+60',  flag: '🇲🇾', name: 'Malaysia' },
  { code: '+52',  flag: '🇲🇽', name: 'Mexico' },
  { code: '+212', flag: '🇲🇦', name: 'Morocco' },
  { code: '+977', flag: '🇳🇵', name: 'Nepal' },
  { code: '+31',  flag: '🇳🇱', name: 'Netherlands' },
  { code: '+64',  flag: '🇳🇿', name: 'New Zealand' },
  { code: '+234', flag: '🇳🇬', name: 'Nigeria' },
  { code: '+47',  flag: '🇳🇴', name: 'Norway' },
  { code: '+968', flag: '🇴🇲', name: 'Oman' },
  { code: '+63',  flag: '🇵🇭', name: 'Philippines' },
  { code: '+48',  flag: '🇵🇱', name: 'Poland' },
  { code: '+351', flag: '🇵🇹', name: 'Portugal' },
  { code: '+974', flag: '🇶🇦', name: 'Qatar' },
  { code: '+40',  flag: '🇷🇴', name: 'Romania' },
  { code: '+7',   flag: '🇷🇺', name: 'Russia' },
  { code: '+27',  flag: '🇿🇦', name: 'South Africa' },
  { code: '+34',  flag: '🇪🇸', name: 'Spain' },
  { code: '+94',  flag: '🇱🇰', name: 'Sri Lanka' },
  { code: '+46',  flag: '🇸🇪', name: 'Sweden' },
  { code: '+41',  flag: '🇨🇭', name: 'Switzerland' },
  { code: '+886', flag: '🇹🇼', name: 'Taiwan' },
  { code: '+255', flag: '🇹🇿', name: 'Tanzania' },
  { code: '+66',  flag: '🇹🇭', name: 'Thailand' },
  { code: '+216', flag: '🇹🇳', name: 'Tunisia' },
  { code: '+90',  flag: '🇹🇷', name: 'Turkey' },
  { code: '+256', flag: '🇺🇬', name: 'Uganda' },
  { code: '+380', flag: '🇺🇦', name: 'Ukraine' },
  { code: '+84',  flag: '🇻🇳', name: 'Vietnam' },
  { code: '+967', flag: '🇾🇪', name: 'Yemen' },
  { code: '+260', flag: '🇿🇲', name: 'Zambia' },
  { code: '+263', flag: '🇿🇼', name: 'Zimbabwe' },
];

export default function RegisterPage() {
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const [form,        setForm]        = useState({ full_name: '', email: '', password: '' });
  const [errors,      setErrors]      = useState({});
  const [loading,     setLoading]     = useState(false);
  const [gLoading,    setGLoading]    = useState(false);
  const [showPass,    setShowPass]    = useState(false);
  const [countryCode, setCountryCode] = useState('+971');
  const [phoneNumber, setPhoneNumber] = useState('');
  const googleClientRef = useRef(null);

  const handleGoogleClick = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      toast.error('Google sign-in is not configured');
      return;
    }
    if (!window.google?.accounts?.oauth2) {
      toast.error('Google sign-in unavailable — please try again in a moment');
      return;
    }
    if (!googleClientRef.current) {
      googleClientRef.current = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: 'openid profile email',
        callback: async (tokenResponse) => {
          if (tokenResponse.error) {
            toast.error('Google sign-in was cancelled');
            return;
          }
          setGLoading(true);
          try {
            const data = await loginWithGoogle(tokenResponse.access_token);
            toast.success(data.is_new_user ? 'Account created! Welcome to CentricQ.' : 'Welcome back!');
            navigate('/dashboard');
          } catch (err) {
            toast.error(err.response?.data?.error || 'Google sign-in failed. Please try again.');
          } finally {
            setGLoading(false);
          }
        },
      });
    }
    googleClientRef.current.requestAccessToken();
  };

  const set = field => e => setForm(f => ({ ...f, [field]: e.target.value }));

  /* live password requirement checks */
  const pwd = form.password;
  const reqs = {
    length:    pwd.length >= 8,
    uppercase: /[A-Z]/.test(pwd),
    number:    /[0-9]/.test(pwd),
    special:   /[^A-Za-z0-9]/.test(pwd),
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = 'Full name is required';
    if (!form.email.trim())     e.email     = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Invalid email address';
    if (form.password.length < 8)          e.password = 'Password must be at least 8 characters';
    else if (!/[A-Z]/.test(form.password)) e.password = 'Must contain an uppercase letter';
    else if (!/[0-9]/.test(form.password)) e.password = 'Must contain a number';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    const phone = phoneNumber.trim()
      ? `${countryCode}${phoneNumber.trim().replace(/[\s\-()]/g, '')}`
      : '';
    try {
      await register(form.full_name, form.email, form.password, phone || undefined);
      toast.success('Account created! Welcome to CentricQ.');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputBorder = hasError => hasError ? '#C1272D' : '#E2E8F0';
  const inputShadow = hasError => hasError ? '0 0 0 3px rgba(193,39,45,0.08)' : undefined;

  return (
    <div className="min-h-screen flex" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        *, *::before, *::after { box-sizing: border-box; }

        .ip-input:focus {
          border-color: #1B3A5C !important;
          box-shadow: 0 0 0 3px rgba(27,58,92,0.10) !important;
          outline: none;
        }
        .ip-input-error:focus {
          border-color: #C1272D !important;
          box-shadow: 0 0 0 3px rgba(193,39,45,0.10) !important;
          outline: none;
        }

        .ip-btn-primary:not(:disabled):hover {
          background: #142d47 !important;
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(27,58,92,0.22);
        }
        .ip-btn-primary { transition: background 200ms, transform 200ms, box-shadow 200ms; }

        .ip-glass {
          background: rgba(255,255,255,0.08);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255,255,255,0.13);
        }
      `}</style>

      {/* ── Left brand panel ─────────────────────────────────────────────── */}
      <div
        className="hidden lg:flex flex-col justify-between flex-shrink-0 px-12 pt-12 pb-10 relative overflow-hidden"
        style={{
          width: 440,
          background: 'linear-gradient(160deg, #0A1929 0%, #0d2238 50%, #1B3A5C 100%)',
        }}
      >
        {/* Logo */}
        <Link to="/" className="no-underline relative z-10">
          <span style={{ fontSize: 22, fontWeight: 800, color: '#FFFFFF', letterSpacing: '-0.02em' }}>
            Centric<span style={{ color: '#C1272D' }}>Q</span>
          </span>
        </Link>

        {/* Content */}
        <div className="relative z-10">
          {/* "Free to start" badge */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            borderRadius: 999, padding: '6px 16px', marginBottom: 20,
            background: 'rgba(193,39,45,0.12)',
            border: '1px solid rgba(193,39,45,0.25)',
          }}>
            <span className="animate-pulse" style={{
              width: 6, height: 6, borderRadius: '50%', background: '#C1272D',
            }} />
            <span style={{ fontSize: 12, fontWeight: 500, color: 'rgba(255,255,255,0.7)' }}>
              Free to start — no card needed
            </span>
          </div>

          <h2 style={{ fontSize: 34, fontWeight: 800, color: '#FFFFFF', letterSpacing: '-0.02em', lineHeight: 1.12, marginBottom: 16 }}>
            Get interview-ready in days, not weeks.
          </h2>
          <p style={{ fontSize: 15, lineHeight: 1.8, color: 'rgba(255,255,255,0.5)', marginBottom: 28 }}>
            Join 10,000+ candidates who used CentricQ to prepare and land their target roles.
          </p>

          {/* Stats grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 28 }}>
            {[
              { value: '150+',  label: 'Job roles'         },
              { value: '1,000', label: 'Questions per role' },
              { value: '3',     label: 'Answer formats'    },
              { value: '100%',  label: 'AI-evaluated'      },
            ].map(({ value, label }) => (
              <div key={label} style={{
                borderRadius: 12, padding: 16,
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}>
                <p style={{ fontSize: 26, fontWeight: 800, color: '#FFFFFF', lineHeight: 1, marginBottom: 4 }}>{value}</p>
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{label}</p>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[
              'Start practicing for free immediately',
              'No credit card required',
              'Full AI feedback on 200 free questions',
            ].map(item => (
              <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 20, height: 20, borderRadius: '50%',
                  background: 'rgba(193,39,45,0.18)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <IconCheck className="w-3 h-3" style={{ color: '#C1272D' }} />
                </div>
                <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.65)' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom testimonial */}
        <div className="ip-glass" style={{ borderRadius: 16, padding: 20 }}>
          <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
            {[1,2,3,4,5].map(s => (
              <svg key={s} style={{ width: 14, height: 14 }} viewBox="0 0 24 24" fill="#C1272D">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
            ))}
          </div>
          <p style={{ fontSize: 14, lineHeight: 1.7, fontStyle: 'italic', color: 'rgba(255,255,255,0.65)', marginBottom: 14 }}>
            "Finished the Data Analyst track in three weeks. Walking into interviews actually prepared made all the difference."
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: '#C1272D',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 12, fontWeight: 700, flexShrink: 0,
            }}>
              PS
            </div>
            <div>
              <p style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.75)' }}>Priya Sharma</p>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>Data Analyst, Careem</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Right form panel ──────────────────────────────────────────────── */}
      <div
        className="flex-1 flex items-center justify-center px-6 py-12"
        style={{ background: '#FFFFFF' }}
      >
        <div style={{ width: '100%', maxWidth: 400 }}>

          {/* Mobile logo */}
          <div className="lg:hidden" style={{ textAlign: 'center', marginBottom: 32 }}>
            <Link to="/" className="no-underline">
              <span style={{ fontSize: 22, fontWeight: 800, color: '#1B3A5C', letterSpacing: '-0.02em' }}>
                Centric<span style={{ color: '#C1272D' }}>Q</span>
              </span>
            </Link>
          </div>

          {/* Social proof line */}
          <p style={{ textAlign: 'center', fontSize: 13, fontWeight: 500, color: '#64748B', marginBottom: 20 }}>
            Join{' '}
            <span style={{ fontWeight: 700, color: '#1B3A5C' }}>10,000+</span>
            {' '}candidates preparing for their dream role
          </p>

          {/* Heading */}
          <div style={{ marginBottom: 28 }}>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em', marginBottom: 6 }}>
              Create your account
            </h1>
            <p style={{ fontSize: 15, color: '#64748B' }}>
              Free to start. No credit card needed.
            </p>
          </div>

          {/* Google sign-up button */}
          <button
            type="button"
            onClick={handleGoogleClick}
            disabled={gLoading || loading}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              padding: '13px 16px',
              background: '#FFFFFF',
              border: '1.5px solid #E2E8F0',
              borderRadius: 10,
              fontSize: 15,
              fontWeight: 600,
              color: '#0F172A',
              cursor: (gLoading || loading) ? 'not-allowed' : 'pointer',
              opacity: (gLoading || loading) ? 0.6 : 1,
              transition: 'border-color 150ms, box-shadow 150ms',
              marginBottom: 20,
            }}
            onMouseEnter={e => { if (!gLoading && !loading) e.currentTarget.style.borderColor = '#1B3A5C'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#E2E8F0'; }}
          >
            {gLoading ? <IconSpinner className="w-4 h-4" /> : <GoogleIcon />}
            {gLoading ? 'Creating account…' : 'Continue with Google'}
          </button>

          {/* Divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{ flex: 1, height: 1, background: '#E2E8F0' }} />
            <span style={{ fontSize: 12, fontWeight: 500, color: '#94A3B8', whiteSpace: 'nowrap' }}>or sign up with email</span>
            <div style={{ flex: 1, height: 1, background: '#E2E8F0' }} />
          </div>

          {/* Form — no card, fields directly on white */}
          <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Full name */}
            <div>
              <label htmlFor="full_name" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                Full name
              </label>
              <input
                id="full_name"
                type="text"
                value={form.full_name}
                onChange={set('full_name')}
                placeholder="Sarah Al Mansouri"
                autoComplete="name"
                className={`ip-input${errors.full_name ? ' ip-input-error' : ''}`}
                style={{
                  width: '100%', display: 'block',
                  padding: '12px 16px', fontSize: 15,
                  border: `1.5px solid ${inputBorder(!!errors.full_name)}`,
                  borderRadius: 10,
                  background: '#FFFFFF', color: '#0F172A',
                  boxShadow: inputShadow(!!errors.full_name),
                  transition: 'border-color 150ms, box-shadow 150ms',
                }}
              />
              {errors.full_name && (
                <p style={{ marginTop: 6, fontSize: 12, fontWeight: 500, color: '#C1272D' }}>
                  {errors.full_name}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                Email address
              </label>
              <input
                id="email"
                type="email"
                value={form.email}
                onChange={set('email')}
                placeholder="you@example.com"
                autoComplete="email"
                className={`ip-input${errors.email ? ' ip-input-error' : ''}`}
                style={{
                  width: '100%', display: 'block',
                  padding: '12px 16px', fontSize: 15,
                  border: `1.5px solid ${inputBorder(!!errors.email)}`,
                  borderRadius: 10,
                  background: '#FFFFFF', color: '#0F172A',
                  boxShadow: inputShadow(!!errors.email),
                  transition: 'border-color 150ms, box-shadow 150ms',
                }}
              />
              {errors.email && (
                <p style={{ marginTop: 6, fontSize: 12, fontWeight: 500, color: '#C1272D' }}>
                  {errors.email}
                </p>
              )}
            </div>

            {/* Phone number */}
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                Phone number
              </label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                {/* Country code dropdown */}
                <div style={{ position: 'relative', flexShrink: 0, width: 110, height: 48 }}>
                  <select
                    value={countryCode}
                    onChange={e => { if (e.target.value) setCountryCode(e.target.value); }}
                    style={{
                      width: '100%',
                      height: 48,
                      padding: '0 28px 0 12px',
                      fontSize: 14,
                      fontWeight: 600,
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                      border: '1.5px solid #E2E8F0',
                      borderRadius: 10,
                      background: '#FFFFFF',
                      color: '#0F172A',
                      cursor: 'pointer',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      outline: 'none',
                      transition: 'border-color 150ms, box-shadow 150ms',
                    }}
                    onFocus={e => { e.target.style.borderColor = '#1B3A5C'; e.target.style.boxShadow = '0 0 0 3px rgba(27,58,92,0.10)'; }}
                    onBlur={e => { e.target.style.borderColor = '#E2E8F0'; e.target.style.boxShadow = 'none'; }}
                  >
                    {COUNTRIES.map((c, i) =>
                      c.code === ''
                        ? <option key={i} value="" disabled>{c.name}</option>
                        : <option key={i} value={c.code}>{c.flag} {c.code}</option>
                    )}
                  </select>
                  {/* Chevron icon */}
                  <svg viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, pointerEvents: 'none' }}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
                {/* Phone number input */}
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={e => setPhoneNumber(e.target.value.replace(/[^0-9\s\-()]/g, ''))}
                  placeholder="50 123 4567"
                  autoComplete="tel-national"
                  className="ip-input"
                  style={{
                    flex: 1,
                    height: 48,
                    padding: '0 16px',
                    fontSize: 15,
                    border: '1.5px solid #E2E8F0',
                    borderRadius: 10,
                    background: '#FFFFFF',
                    color: '#0F172A',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                    transition: 'border-color 150ms, box-shadow 150ms',
                  }}
                />
              </div>
              <p style={{ marginTop: 5, fontSize: 11, color: '#94A3B8' }}>
                Used for account updates and relevant offers. Never sold or shared.
              </p>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  id="password"
                  type={showPass ? 'text' : 'password'}
                  value={form.password}
                  onChange={set('password')}
                  placeholder="Min. 8 characters"
                  autoComplete="new-password"
                  className={`ip-input${errors.password ? ' ip-input-error' : ''}`}
                  style={{
                    width: '100%', display: 'block',
                    padding: '12px 48px 12px 16px', fontSize: 15,
                    border: `1.5px solid ${inputBorder(!!errors.password)}`,
                    borderRadius: 10,
                    background: '#FFFFFF', color: '#0F172A',
                    boxShadow: inputShadow(!!errors.password),
                    transition: 'border-color 150ms, box-shadow 150ms',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPass(v => !v)}
                  aria-label={showPass ? 'Hide password' : 'Show password'}
                  style={{
                    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', padding: 4,
                    color: '#94A3B8', cursor: 'pointer', transition: 'color 150ms',
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = '#1B3A5C'}
                  onMouseLeave={e => e.currentTarget.style.color = '#94A3B8'}
                >
                  {showPass ? <IconEyeOff className="w-4 h-4" /> : <IconEye className="w-4 h-4" />}
                </button>
              </div>

              {/* Live password requirements — 2×2 grid */}
              {(form.password.length > 0 || errors.password) && (
                <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px' }}>
                  <PwdReq met={reqs.length}   label="At least 8 characters" />
                  <PwdReq met={reqs.uppercase} label="One uppercase letter" />
                  <PwdReq met={reqs.number}    label="One number" />
                  <PwdReq met={reqs.special}   label="One special character" />
                </div>
              )}
              {errors.password && !form.password && (
                <p style={{ marginTop: 6, fontSize: 12, fontWeight: 500, color: '#C1272D' }}>
                  {errors.password}
                </p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="ip-btn-primary"
              style={{
                width: '100%',
                padding: '14px',
                background: '#1B3A5C',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.55 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              {loading && <IconSpinner className="w-4 h-4" />}
              {loading ? 'Creating account…' : 'Create free account'}
            </button>

          </form>

          {/* T&C note */}
          <p style={{ marginTop: 16, fontSize: 12, textAlign: 'center', lineHeight: 1.6, color: '#94A3B8' }}>
            By signing up, you agree to our{' '}
            <Link to="/terms" className="no-underline" style={{ color: '#1B3A5C', transition: 'color 150ms' }}
              onMouseEnter={e => e.target.style.color = '#C1272D'}
              onMouseLeave={e => e.target.style.color = '#1B3A5C'}>
              Terms
            </Link>
            {' '}and{' '}
            <Link to="/privacy" className="no-underline" style={{ color: '#1B3A5C', transition: 'color 150ms' }}
              onMouseEnter={e => e.target.style.color = '#C1272D'}
              onMouseLeave={e => e.target.style.color = '#1B3A5C'}>
              Privacy Policy
            </Link>.
          </p>

          {/* Footer link */}
          <p style={{ textAlign: 'center', marginTop: 24, fontSize: 14, color: '#64748B' }}>
            Already have an account?{' '}
            <Link
              to="/login"
              className="no-underline"
              style={{ fontWeight: 600, color: '#1B3A5C', transition: 'color 150ms' }}
              onMouseEnter={e => e.target.style.color = '#C1272D'}
              onMouseLeave={e => e.target.style.color = '#1B3A5C'}
            >
              Sign in →
            </Link>
          </p>

        </div>
      </div>
    </div>
  );
}
